import api from './client';

export interface PennylaneStatus {
  connected: boolean;
  /** Pennylane company ID — public-ish identifier used for deep-links to
   *  the Pennylane UI. Returned only via this endpoint. Null when not
   *  configured. */
  company_id?: string | null;
}

export const pennylaneApi = {
  // --- Credentials (PAT-based, one set per org) ---

  /** Save the org's Pennylane Personal Access Token + company ID. The
   *  PAT is validated against Pennylane before being persisted, and is
   *  never echoed back in any response. */
  saveCredentials: async (creds: { pat: string; company_id: string }) => {
    const { data } = await api.put('/pennylane/credentials', creds);
    return data as PennylaneStatus;
  },

  /** Clear the stored credentials. Billing actions will fail with 424
   *  until new credentials are saved. */
  clearCredentials: async () => {
    const { data } = await api.delete('/pennylane/credentials');
    return data as PennylaneStatus;
  },

  /** Connection status + company_id (no PAT). */
  getStatus: async () => {
    const { data } = await api.get('/pennylane/status');
    return data as PennylaneStatus;
  },

  // --- Clients ---

  /**
   * List or search Pennylane customers
   * @param search - Optional search term
   */
  listClients: async (search?: string) => {
    const { data } = await api.post('/pennylane/clients', { search });
    return data;
  },

  /**
   * Get a single Pennylane customer by ID
   * @param clientId - Pennylane customer ID
   */
  getClient: async (clientId: number) => {
    const { data } = await api.get(`/pennylane/clients/${clientId}`);
    return data;
  },

  /**
   * Create a company customer in Pennylane
   * @param clientData - Company data
   */
  createCompanyClient: async (clientData: Record<string, any>) => {
    const { data } = await api.post('/pennylane/clients/company', clientData);
    return data;
  },

  /**
   * Create an individual customer in Pennylane
   * @param clientData - Individual data
   */
  createIndividualClient: async (clientData: Record<string, any>) => {
    const { data } = await api.post('/pennylane/clients/individual', clientData);
    return data;
  },

  /**
   * Update a Pennylane customer
   * @param clientId - Pennylane customer ID
   * @param clientData - Fields to update
   */
  updateClient: async (clientId: number, clientData: Record<string, any>) => {
    const { data } = await api.put(`/pennylane/clients/${clientId}`, clientData);
    return data;
  },

  // --- Invoices ---

  /**
   * Create a draft invoice in Pennylane
   * @param invoiceData - Invoice payload
   */
  createDraftInvoice: async (invoiceData: Record<string, any>) => {
    const { data } = await api.post('/pennylane/invoices', invoiceData);
    return data;
  },

  /**
   * Get invoice details by ID
   * @param invoiceId - Pennylane invoice ID
   */
  getInvoice: async (invoiceId: number) => {
    const { data } = await api.get(`/pennylane/invoices/${invoiceId}`);
    return data;
  },

  /**
   * Finalize a draft invoice (irreversible)
   * @param invoiceId - Pennylane invoice ID
   */
  finalizeInvoice: async (invoiceId: number) => {
    const { data } = await api.put(`/pennylane/invoices/${invoiceId}/finalize`);
    return data;
  },

  /**
   * Delete a draft invoice
   * @param invoiceId - Pennylane invoice ID
   */
  deleteInvoice: async (invoiceId: number) => {
    const { data } = await api.delete(`/pennylane/invoices/${invoiceId}`);
    return data;
  },

  // --- Quotes ---

  /**
   * Create a quote in Pennylane
   * @param quoteData - Quote payload
   */
  createQuote: async (quoteData: Record<string, any>) => {
    const { data } = await api.post('/pennylane/quotes', quoteData);
    return data;
  },

  /**
   * Get quote details by ID
   * @param quoteId - Pennylane quote ID
   */
  getQuote: async (quoteId: number) => {
    const { data } = await api.get(`/pennylane/quotes/${quoteId}`);
    return data;
  },

  /**
   * Update an existing quote
   * @param quoteId - Pennylane quote ID
   * @param quoteData - Updated quote data
   */
  updateQuote: async (quoteId: number, quoteData: Record<string, any>) => {
    const { data } = await api.put(`/pennylane/quotes/${quoteId}`, quoteData);
    return data;
  },

  /**
   * Delete a quote
   * @param quoteId - Pennylane quote ID
   */
  deleteQuote: async (quoteId: number) => {
    const { data } = await api.delete(`/pennylane/quotes/${quoteId}`);
    return data;
  },

  /**
   * Update quote status (accepted / denied)
   * @param quoteId - Pennylane quote ID
   * @param status - "accepted" or "denied"
   */
  updateQuoteStatus: async (quoteId: number, status: string) => {
    const { data } = await api.put(`/pennylane/quotes/${quoteId}/status`, { status });
    return data;
  },

  /**
   * Convert an accepted quote into a draft invoice
   * @param quoteId - Pennylane quote ID
   */
  convertQuoteToInvoice: async (quoteId: number) => {
    const { data } = await api.post(`/pennylane/quotes/${quoteId}/convert-to-invoice`);
    return data;
  },

  // --- Products ---

  /**
   * List products with cursor-based pagination
   * @param cursor - Pagination cursor (omit for first page)
   */
  listProducts: async (cursor?: string) => {
    const { data } = await api.get('/pennylane/products', { params: cursor ? { cursor } : {} });
    return data;
  },

  /**
   * Create a product in Pennylane
   * @param productData - Product payload
   */
  createProduct: async (productData: Record<string, any>) => {
    const { data } = await api.post('/pennylane/products', productData);
    return data;
  },

  /**
   * Update a product
   * @param productId - Pennylane product ID
   * @param productData - Fields to update
   */
  updateProduct: async (productId: number, productData: Record<string, any>) => {
    const { data } = await api.put(`/pennylane/products/${productId}`, productData);
    return data;
  },

  // --- Reference Data ---

  /**
   * List ledger (plan comptable) accounts
   */
  listLedgerAccounts: async () => {
    const { data } = await api.get('/pennylane/ledger-accounts');
    return data;
  },

  /**
   * List customer invoice PDF templates
   */
  listInvoiceTemplates: async () => {
    const { data } = await api.get('/pennylane/invoice-templates');
    return data;
  },
};
